import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetEntityTimeseriesQuery, useGetSaveRuntimeSessionQuery } from '../../../api/simulationApi';
import { BED_TIMESERIES_PROPERTIES } from '../constants';
import type { BedTimeseriesEntry } from '../../../types/simulation';
import { extractWeatherGap } from '../../../components/weather/extractWeatherGap';
import { useWorldOptional } from '../../../contexts/WorldContext';

/**
 * Load bed-level timeseries from the active sim session.
 */
export const useBedTimeseries = (bedId: string | undefined, saveId: string | undefined) => {
  const world = useWorldOptional();
  const isScenario = world?.type === 'scenario';
  const { data: activeSession } = useGetSaveRuntimeSessionQuery(
    !isScenario && saveId ? saveId : skipToken,
  );

  const sessionId = activeSession?.id;

  const { data, isLoading, isFetching, error, refetch } = useGetEntityTimeseriesQuery(
    sessionId && bedId
      ? {
          sessionId,
          entityType: 'bed',
          entityId: bedId,
          properties: [...BED_TIMESERIES_PROPERTIES],
        }
      : skipToken,
  );

  const weatherGap = extractWeatherGap(error);
  const scenarioTimeSeries = useMemo<BedTimeseriesEntry[]>(() => {
    if (!isScenario || !bedId) return [];
    return (world?.runtime.timeline || []).flatMap((snapshot) => {
      const bed = snapshot.beds?.[bedId];
      return bed ? [{ date: snapshot.simDate, ...bed }] : [];
    });
  }, [bedId, isScenario, world?.runtime.timeline]);
  const timeSeries = isScenario
    ? scenarioTimeSeries
    : (data?.timeSeries ?? []) as unknown as BedTimeseriesEntry[];

  return {
    timeSeries,
    startDate: isScenario ? timeSeries[0]?.date : data?.startDate,
    endDate: isScenario ? timeSeries[timeSeries.length - 1]?.date : data?.endDate,
    totalDays: isScenario ? timeSeries.length : data?.totalDays ?? 0,
    wateringAutomation: isScenario ? undefined : data?.wateringAutomation,
    sessionId: sessionId ?? null,
    isLoading: isScenario ? false : isLoading,
    isFetching: isScenario ? false : isFetching,
    hasSession: isScenario ? timeSeries.length > 0 : !!sessionId,
    error,
    weatherGap,
    refetch: isScenario ? undefined : refetch,
  };
};
